import React, {
  useCallback,
  useContext,
  useRef,
  useState,
  useEffect,
} from "react";
import { NavigationMenu } from "../shared/NavigationMenu";
import { AccountBabies } from "./common/AccountBabies";
import { AccountOwner } from "./common/AccountName";
import { AccountPicture } from "./common/AccountPicture";
import { SettingsCard } from "./common/SettingsCard";
import { SettingsHeader } from "./common/SettingsHeader";
import { SharedButton } from "./common/shared/SharedButton";
import deleteIcon from "../../images/deleteIcon.svg";
import congratsImage from "../../images/congrats.svg";
import { IDetails, InformationSection } from "./common/InformationSection";
import { AuthContext } from "../../context/AuthContext";
import { DeleteAccountConfirmation } from "./common/DeleteAccountConfirmation";
import {
  deleteAccount,
  getSubscriptionOwnership,
  syncNativePurchase,
  transferSubscriptionOwnership,
} from "../../api";
import { SubscriptionUpgrade } from "../shared/SubscriptionUpgrade";
import { PlanChoices, billingPeriodLabel } from "../shared/PlanChoices";
import { useBabyContext } from "../../context/BabyContext";
import { useNavigate } from "react-router-dom";
import { InvitationList } from "../profile/common/InvitationList";
import {
  fetchCatalog,
  identifyUser,
  isNative,
  nativePurchasesAvailable,
  onNative,
  restorePurchases,
  startPurchase,
  supportsNativePurchase,
} from "../../common/native";

export const SettingsPage = () => {
  const navigate = useNavigate();
  const location = window.location;
  const authContext = useContext(AuthContext);
  const {
    logout,
    authUser,
    hasActivePayment,
    firebaseUid,
    refreshEntitlement,
  } = authContext;
  /* Only the admin of the selected baby can buy a subscription that unlocks
     it. A visitor on somebody else's baby is told who to ask instead of being
     sold something that would leave that baby exactly as locked as it is. */
  const { selectedBaby, isBabyAdmin, babyAdminName } = useBabyContext();
  const viewingSomeoneElsesBaby = isBabyAdmin === false;
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  /* Null until the shell has answered. Purchase stays disabled until then —
     identity has to reach RevenueCat before a purchase can be attributed. */
  const [nativeProduct, setNativeProduct] = useState<any>(null);
  /* Every plan the store offers, not just the first. With a yearly plan
     alongside the monthly one, picking packages[0] would sell whichever the
     store happened to list first and describe it as monthly. */
  const [nativePackages, setNativePackages] = useState<any[]>([]);
  const [purchaseNote, setPurchaseNote] = useState("");
  /* The store's own subscription settings page. Apple does not allow an app to
     cancel a subscription itself, so linking out is the only route available. */
  const [managementUrl, setManagementUrl] = useState<string | null>(null);
  /* Whether the store subscription on this device belongs to this account. The
     server never says *which* account owns it, so this can only ever offer to
     move it here — it cannot name who would lose it. */
  const [ownership, setOwnership] = useState<{
    hasStoreSubscription: boolean;
    ownedByCaller: boolean;
    ownedByAnotherAccount: boolean;
  } | null>(null);

  /* Each call is a live RevenueCat read on the server — the same quota the
     reconciliation job throttles itself to protect. Asking once per visit is
     enough; the answer only changes when the customer does something, and every
     one of those paths asks again explicitly. */
  const ownershipAsked = useRef(false);

  const refreshOwnership = useCallback(async () => {
    if (!supportsNativePurchase()) return null;
    try {
      const { data } = await getSubscriptionOwnership();
      setOwnership(data);
      return data;
    } catch (error) {
      /* An ownership answer we failed to get is not an answer that the
         subscription belongs to somebody else. Leaving the previous state alone
         stops a network blip from telling a paying customer their subscription
         is on another account. */
      console.warn("Could not resolve subscription ownership.", error);
      return null;
    }
  }, []);

  /* Only worth asking while this account has no access. Once they are entitled
     it makes no difference who the store attached the receipt to first. */
  useEffect(() => {
    if (hasActivePayment || !firebaseUid || ownershipAsked.current) return;
    ownershipAsked.current = true;
    refreshOwnership();
  }, [hasActivePayment, firebaseUid, refreshOwnership]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const subscriptionFailedParam = params.get("subscription-failed");

    if (subscriptionFailedParam !== null) {
      subscriptionFailedParam === "false"
        ? navigate("/feedback", {
            state: {
              headerText: "Subscription Successful!",
              bodyText: "You have subscribed to the new plan.",
              buttonText: "Close",
              navigateTo: "/settings",
              image: congratsImage,
              success: true,
            },
          })
        : navigate("/feedback", {
            state: {
              headerText: "Subscription Failed!",
              bodyText: "Please go back and try again.",
              buttonText: "Go Back",
              navigateTo: "/settings",
              image: "",
              success: false,
            },
          });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, refresh]);
  const accountInformationData: IDetails[] = [
    // { title: "Your plan", value: "Freemium" },
    { title: "Your E-mail", value: authUser?.email || "No Data" },
    // { title: "Your password", value: "********" },
  ];

  // const personalInformationData: IDetails[] = [
  //   { title: "Place of Birth", value: "No Data" },
  //   { title: "Date of Birth", value: "No Data" },
  //   { title: "Spouse", value: "No Data" },
  // ];
  const handleDeleteAccount = async () => {
    setLoading(true);
    try {
      await deleteAccount().then(() => {
        logout();
      });
      setLoading(false);
    } catch (error) {}
  };

  /* Identify to the store, then load the catalog. The identify reply is awaited
     rather than fired and forgotten: RevenueCat's login is a network call, and a
     purchase landing before it completes is attributed to nobody and cannot be
     recovered, even by Restore. */
  useEffect(() => {
    if (!supportsNativePurchase() || !firebaseUid) return;
    let cancelled = false;
    (async () => {
      const identified = await identifyUser(firebaseUid);
      if (cancelled || identified?.status !== "success") return;
      if (identified.managementUrl) setManagementUrl(identified.managementUrl);
      const catalog = await fetchCatalog();
      if (cancelled || catalog?.status !== "success") return;
      const packages = catalog.packages ?? [];
      setNativePackages(packages);
      /* Default selection: the monthly plan when the store offers one, so the
         cheapest commitment is preselected rather than whichever plan the
         store happened to return first. */
      setNativeProduct(
        packages.find((p: any) => p.packageType === "MONTHLY") ??
          packages[0] ??
          null
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [firebaseUid]);

  /* The shell nudges after a purchase settles, including ones that complete
     while the app was closed. */
  useEffect(
    () =>
      onNative("entitlement.refresh", (payload: any) => {
        if (payload?.managementUrl) setManagementUrl(payload.managementUrl);
        refreshEntitlement();
      }),
    [refreshEntitlement]
  );

  const handleUpgradeClick = async () => {
    /* Stripe checkout is retired. Subscriptions are sold through the stores
       only, so the browser has nothing to sell — it explains where to go rather
       than starting a checkout we no longer honour. Existing Stripe subscribers
       keep their access: the server still resolves legacy payments. */
    if (!isNative()) {
      setPurchaseNote(
        "Subscriptions are managed in the Little Feather app. Open the app on your phone to subscribe."
      );
      return;
    }

    /* A shell whose platform has no store configured. Distinct from an old
       shell, because updating will not help — and telling someone to update to
       fix something an update cannot fix is worse than saying nothing. */
    if (!nativePurchasesAvailable()) {
      setPurchaseNote(
        "Subscriptions are not available in this version of the app yet."
      );
      return;
    }

    /* Native shell that predates the bridge: never offer web checkout here —
       that is the guideline violation this change exists to remove. */
    if (!supportsNativePurchase()) {
      setPurchaseNote(
        "Please update the Little Feather app from the App Store or Google Play to subscribe."
      );
      return;
    }

    if (!nativeProduct) {
      setPurchaseNote(
        "Subscription options are still loading — try again in a moment."
      );
      return;
    }

    setButtonLoading(true);
    setPurchaseNote("");
    const result = await startPurchase(nativeProduct.productId);
    setButtonLoading(false);

    if (result?.status === "success") {
      /* Tell the server to go and look now. The webhook is the durable path, but
         it lands seconds to a minute later, and the customer is staring at the
         paywall in the meantime. A failure here is not fatal — the webhook still
         settles it — so it must never turn a completed purchase into an error. */
      try {
        await syncNativePurchase();
      } catch (err) {
        console.warn("Entitlement sync failed; webhook will reconcile.", err);
      }
      await refreshEntitlement();
      return;
    }
    if (result?.status === "cancelled") return;
    /* The store account is already paying, just against a different Little
       Feather account. Saying "that didn't go through" would be actively wrong —
       they are subscribed, and telling them to try again invites a second
       payment attempt that will be refused in exactly the same way. */
    if (result?.status === "already_owned") {
      /* The store refused because the receipt is spoken for. Ask our own server
         who holds it, so the reply can offer to move it here rather than
         leaving a dead end. */
      const owned = await refreshOwnership();
      setPurchaseNote(
        owned?.ownedByAnotherAccount
          ? ""
          : "This App Store account already has a Little Feather subscription on a different account. Sign in to that account, or use a different App Store account to subscribe here."
      );
      return;
    }
    if (result?.status === "pending") {
      /* Ask to Buy and deferred payments settle later, sometimes on another
         launch. Never present this as a failure. */
      setPurchaseNote(
        "Your purchase is pending approval. Access unlocks once it completes."
      );
      return;
    }
    setPurchaseNote("That didn't go through. No payment was taken.");
  };

  const handleRestoreClick = async () => {
    setButtonLoading(true);
    setPurchaseNote("");
    const result = await restorePurchases();
    /* Restore has the same problem as a fresh purchase: the store knows, and our
       server does not until something tells it to look. */
    if (result?.entitled) {
      try {
        await syncNativePurchase();
      } catch (err) {
        console.warn("Entitlement sync failed; webhook will reconcile.", err);
      }
    }
    await refreshEntitlement();
    /* Asked whatever the store said, because "the store restored it but you
       still have no access" and "the store refused the restore" are the same
       situation seen from the customer's side — the subscription is attached to
       another Little Feather account. Only our server can tell them apart. */
    const owned = await refreshOwnership();
    setButtonLoading(false);

    /* The panel below explains this case in full; a second, shorter message
       saying something slightly different would only muddle it. */
    if (owned?.ownedByAnotherAccount) return;

    setPurchaseNote(
      result?.entitled
        ? "Your subscription has been restored."
        : "No previous purchase was found for this account."
    );
  };

  /* Moving a subscription takes paid access away from the account that has it,
     so this is only ever offered — never done automatically on a restore. */
  const handleTransferClick = async () => {
    setButtonLoading(true);
    setPurchaseNote("");
    try {
      const { data } = await transferSubscriptionOwnership();
      setPurchaseNote(
        data?.status === "already_owned"
          ? "This subscription is already on your account."
          : "This subscription is now on your account."
      );
      /* Set locally rather than waited for. refreshOwnership deliberately keeps
         the old answer when it cannot reach the server, which is right when the
         answer is unknown — but here it is known, because the transfer just
         succeeded. Without this, one network blip leaves "now on your account"
         sitting directly above a panel saying it belongs to someone else. */
      setOwnership((current) =>
        current
          ? { ...current, ownedByCaller: true, ownedByAnotherAccount: false }
          : current
      );
      await refreshEntitlement();
      await refreshOwnership();
    } catch (error: any) {
      /* The store has no subscription to move, so the ownership record we are
         acting on is stale. Clearing it puts Restore back on screen: without
         that the customer is left with a panel offering to move something that
         does not exist, and no other action — the one state where they would
         have no self-service route at all. */
      if (error?.response?.data?.code === "no_store_subscription") {
        setOwnership(null);
      }
      /* The server owns the wording of a refusal — it is the side that knows
         how many days are left on the cooldown, and duplicating that arithmetic
         here is how the two ends start disagreeing. */
      setPurchaseNote(
        error?.response?.data?.error ??
          "We could not move the subscription just now. Please try again."
      );
    } finally {
      setButtonLoading(false);
    }
  };

  return (
    <React.Fragment>
      <SettingsCard>
        <SettingsHeader />
        {!hasActivePayment && (
          <SubscriptionUpgrade
            margin="0px"
            /* In a browser there is nothing to buy: subscriptions are sold
               only as in-app purchases, so the browser points to the app
               instead of offering a button that can only say so afterwards. */
            hideAction={viewingSomeoneElsesBaby || !isNative()}
            text={
              viewingSomeoneElsesBaby
                ? babyAdminName
                  ? `${
                      selectedBaby?.name ?? "This baby"
                    } is looked after by ${babyAdminName}. Ask them to upgrade and everything here unlocks for all of you.`
                  : `This baby belongs to another account. Ask whoever set it up to upgrade, and everything here unlocks for all of you.`
                : !isNative()
                  ? "Little Feather Pro is bought in the Little Feather app. Get the app, sign in with this account and subscribe there — everything unlocks here too."
                  : nativeProduct
                    ? /* The store's own localised price string, rendered verbatim.
                       Apps get rejected when in-app pricing and the purchase sheet
                       disagree, so this is never reformatted or derived. The
                       period comes from the package, not from an assumption. */
                      `${nativeProduct.title} — ${
                        nativeProduct.displayPrice
                      } ${billingPeriodLabel(
                        nativeProduct
                      )}, renews automatically until cancelled.`.replace(
                        "  ",
                        " "
                      )
                    : `You are currently not subscribed to our app. Please subscribe to access all features.`
            }
            onClick={handleUpgradeClick}
            disabled={buttonLoading}
          />
        )}
        {!hasActivePayment && !viewingSomeoneElsesBaby && !isNative() && (
          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "center",
              margin: "12px 0 4px",
              fontSize: "14px",
            }}
          >
            <a
              href="https://apps.apple.com/app/id6444778069"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#7a5038" }}
            >
              App Store
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.littlefeather.app"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#7a5038" }}
            >
              Google Play
            </a>
          </div>
        )}
        {/* Only shown when the store actually offers more than one plan, so
            nothing changes for anyone until the yearly product is live. */}
        {!hasActivePayment &&
          !viewingSomeoneElsesBaby &&
          nativePackages.length > 1 && (
            <PlanChoices
              packages={nativePackages}
              selectedProductId={nativeProduct?.productId}
              onSelect={setNativeProduct}
            />
          )}
        {/* Named rather than described: the customer is being told their own
            subscription is on an account they may have forgotten they made, and
            a vague "something went wrong" is what sends them to support. */}
        {ownership?.ownedByAnotherAccount && (
          <div
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              padding: "12px",
              margin: "8px 0",
            }}
          >
            <p style={{ fontSize: "14px", margin: "0 0 8px 0" }}>
              This App Store subscription is already connected to a different
              Little Feather account. Restoring it here will not work while it
              belongs to that account.
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#7d7d7d",
                margin: "0 0 12px 0",
              }}
            >
              If that account is also yours, you can move the subscription here.
              The other account loses access straight away, and a subscription
              can only be moved once every 30 days.
            </p>
            <button
              type="button"
              onClick={handleTransferClick}
              disabled={buttonLoading}
              style={{
                background: "none",
                border: "1px solid #7d7d7d",
                borderRadius: "6px",
                padding: "8px 12px",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              Move subscription to this account
            </button>
          </div>
        )}
        {/* Restore has to be reachable from the paywall itself, not buried in a
            sub-page — reviewers look for it and tap it. Hidden only in the one
            case where it provably cannot succeed, so that the panel above is the
            single answer on screen. */}
        {supportsNativePurchase() && !ownership?.ownedByAnotherAccount && (
          <button
            type="button"
            onClick={handleRestoreClick}
            disabled={buttonLoading}
            style={{
              background: "none",
              border: "none",
              padding: "8px 0",
              color: "#7d7d7d",
              textDecoration: "underline",
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            Restore Purchases
          </button>
        )}
        {/* Apple requires both links on the purchase screen itself. Plain
            static pages, so they also open on a desktop, where App Review
            checks them. */}
        {!hasActivePayment && (
          <div
            style={{
              display: "flex",
              gap: "16px",
              justifyContent: "center",
              fontSize: "13px",
            }}
          >
            <a href="/terms.html" style={{ color: "#7d7d7d" }}>
              Terms of Use
            </a>
            <a href="/privacy.html" style={{ color: "#7d7d7d" }}>
              Privacy Policy
            </a>
          </div>
        )}
        {/* Apple forbids an app from cancelling a subscription itself, so this
            links out to the store's own settings. App Review looks for it. */}
        {hasActivePayment && managementUrl && (
          <a
            href={managementUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-block",
              padding: "8px 0",
              color: "#7d7d7d",
              fontSize: "14px",
              textUnderlineOffset: "3px",
            }}
          >
            Manage subscription
          </a>
        )}
        {purchaseNote && (
          <p style={{ fontSize: "14px", color: "#7d7d7d", margin: "4px 0" }}>
            {purchaseNote}
          </p>
        )}
        <AccountPicture />
        <AccountOwner />
        <AccountBabies refresh={refresh} />
        <InvitationList onRefresh={() => setRefresh(!refresh)} />

        <InformationSection
          title="Account Information"
          data={accountInformationData}
          showEditIcon={false}
        />
        {/* <InformationSection
          title="Your personal information"
          data={personalInformationData}
        /> */}
        <SharedButton
          iconSrc={deleteIcon}
          buttonText="Delete Account"
          $margin="36px 0 0 0"
          onClick={() => setShowModal(true)}
        />
        <SharedButton
          buttonText="Log Out"
          onClick={() => {
            logout();
          }}
          $margin="20px 0 0 0"
        />
      </SettingsCard>
      <NavigationMenu />
      {showModal && (
        <DeleteAccountConfirmation
          showModal={showModal}
          handleSetShowModal={() => setShowModal(!showModal)}
          title="Confirmation"
          handleActionClick={handleDeleteAccount}
          loading={loading}
          paragraphText="This action will permanently delete your account and all related
          models. This cannot be undone."
          headerText="Are you sure you want to proceed?"
          subscriptionWarning={hasActivePayment}
          manageSubscriptionUrl={managementUrl}
        />
      )}
    </React.Fragment>
  );
};
